import { lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { ThemeProvider } from "@/components/layout/ThemeProvider";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Layout } from "@/components/layout/Layout";
import { PageSkeleton } from "@/components/ui/page-skeleton";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Login } from "@/pages/Login";
import { Users } from "@/pages/Users";
import { UserDetails } from "@/pages/UserDetails";
import { useAuthStore } from "@/store/auth";
import "./App.css";

// Lazy-loaded routes (in priority order by bundle impact)
const Settings = lazy(() => import("@/pages/Settings")); // ~50KB
const VisitorDashboard = lazy(
  () => import("@/pages/Visitors/VisitorDashboard"),
); // ~80KB
const VisitorHistory = lazy(() => import("@/pages/Visitors/VisitorHistory"));
const VisitorReports = lazy(() => import("@/pages/Visitors/VisitorReports"));
const StaticQR = lazy(() => import("@/pages/StaticQR")); // ~60KB
const PlatformConfig = lazy(() => import("@/pages/PlatformConfig"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

function App() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);

  const getDefaultRoute = () => {
    if (!user) return "/users";

    if (user.subscriptionType === "visitor_management_only") {
      return user.portalConfig?.default_view || "/visitors";
    }

    return "/users";
  };

  return (
    <ThemeProvider defaultTheme="dark">
      <QueryClientProvider client={queryClient}>
        <Router>
          <div className="App">
            <ErrorBoundary>
              <Suspense fallback={<PageSkeleton />}>
                <Routes>
                  <Route
                    path="/login"
                    element={
                      isAuthenticated ? (
                        <Navigate to={getDefaultRoute()} replace />
                      ) : (
                        <Login />
                      )
                    }
                  />

                  {/* Visitor Management Routes */}
                  <Route
                    path="/visitors"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <VisitorDashboard />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/visitors/history"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <VisitorHistory />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/visitors/reports"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <VisitorReports />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />

                  {/* Standard Platform Routes */}
                  <Route
                    path="/users"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <Users />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/users/:userId"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <UserDetails />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/qr"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <StaticQR />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/dashboard"
                    element={<Navigate to="/users" replace />}
                  />
                  <Route
                    path="/requests"
                    element={<Navigate to="/users" replace />}
                  />
                  <Route
                    path="/webhooks"
                    element={<Navigate to="/qr" replace />}
                  />
                  <Route
                    path="/analytics"
                    element={<Navigate to="/users" replace />}
                  />
                  <Route
                    path="/settings"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <Settings />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/platform-config"
                    element={
                      <ProtectedRoute adminOnly>
                        <Layout>
                          <PlatformConfig />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/"
                    element={<Navigate to={getDefaultRoute()} replace />}
                  />
                </Routes>
              </Suspense>
            </ErrorBoundary>
            <Toaster richColors position="top-right" />
          </div>
        </Router>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
